import { App, Button, Collapse, Divider, Empty, Spin } from "antd"
import React, { Suspense, lazy, useState } from "react"
import { useNavigate } from "react-router-dom"

import { FilePreviewModal } from "../../../../../components/FilePreviewModal/FilePreviewModal.tsx"
import { RoutesPaths } from "../../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { showErrorNotifications } from "../../../../../utils/handlers/handle-error"
import { fetchProposalDocumentById } from "../../../../DocumentTemplates/redux/thunks/document-proposal.thunks.ts"
import { fetchDocumentPreview } from "../../../../DocumentTemplates/redux/thunks/document-templates.thunks.ts"
import {
  getProposalById,
  previewProposal
} from "../../../../Proposals/api/services"
import {
  duplicateProposal,
  emailProposalToClient,
  markProposalAsSent,
  submitProposalForReview
} from "../../../../Proposals/redux/proposals.thunks.ts"
import type { ProposalRequestData } from "../../../../Proposals/redux/proposals.thunks.types.ts"
import { type Proposal } from "../../../../Proposals/types/types.ts"
import type {
  LeadDocumentAttachment,
  LeadProposal
} from "../../../types/lead.documents.types.ts"
import { LeadViewProposalAttachments } from "./LeadViewProposalAttachments.tsx"
import { LeadViewProposalItem } from "./LeadViewProposalItem.tsx"

const LeadViewProposalView = lazy(
  () => import("../LeadViewDocument/LeadViewDocumentView.tsx")
)

interface ProposalVersionsProps {
  isUpdateAllowed: boolean
  viewOnly: boolean
  proposals?: LeadProposal[] | null
}

const INITIAL_VISIBLE_COUNT = 5

export const LeadViewProposals = ({
  isUpdateAllowed,
  viewOnly,
  proposals
}: ProposalVersionsProps) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { modal } = App.useApp()

  const [previewFile, setPreviewFile] = useState<null | LeadDocumentAttachment>(
    null
  )
  const [previewHTML, setPreviewHTML] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  const totalCount = proposals?.length ?? 0
  const hasMore = totalCount > INITIAL_VISIBLE_COUNT
  const hiddenCount = totalCount - INITIAL_VISIBLE_COUNT

  const initialProposals = proposals?.slice(0, INITIAL_VISIBLE_COUNT)
  const additionalProposals = proposals?.slice(INITIAL_VISIBLE_COUNT)

  const onSendToReview = async (proposalId: string, attachmentId: string) => {
    await dispatch(submitProposalForReview({ proposalId, attachmentId }))
  }

  const onMarkAsSent = async (proposalId: string) => {
    await dispatch(markProposalAsSent(proposalId))
  }

  const onShare = async (proposal: LeadProposal) => {
    await dispatch(emailProposalToClient(proposal.id))
  }

  const onViewProposal = async (proposal: LeadProposal) => {
    try {
      const proposal_id = proposal?.id

      if (proposal?.is_version_2) {
        const document = await dispatch(
          fetchProposalDocumentById(proposal_id)
        )?.unwrap()
        const previewHTML = await dispatch(
          fetchDocumentPreview({ ...document, proposal_id })
        )?.unwrap()

        setPreviewHTML(previewHTML)
      } else {
        const proposalRes = await getProposalById(proposal_id)
        if (!proposalRes?.ok) throw new Error("Failed to fetch proposal")
        const fullProposal = proposalRes?.data as Proposal

        const previewRes = await previewProposal({
          proposal_id,
          visible_blocks:
            fullProposal?.visible_blocks as ProposalRequestData["visible_blocks"],
          ...fullProposal?.proposal_data
        })
        if (!previewRes?.ok) throw new Error("Failed to fetch proposal preview")

        const html = previewRes?.data as string
        if (!html) throw new Error("Failed to fetch proposal preview HTML")

        setPreviewHTML(html)
      }
    } catch (e: unknown) {
      showErrorNotifications(e)
    }
  }

  const onDuplicateProposal = async (proposal: LeadProposal) => {
    modal.confirm({
      centered: true,
      title: "Confirm duplication",
      content: "Are you sure you want to duplicate this proposal?",
      cancelText: "Cancel",
      okText: "Duplicate",
      onOk: async () => {
        const newProposal = await dispatch(
          duplicateProposal(proposal?.id)
        )?.unwrap()

        if (!newProposal) return

        const href = proposal?.is_version_2
          ? RoutesPaths.proposalDocument.edit(newProposal?.id)
          : RoutesPaths.proposal.edit(newProposal?.id)

        navigate(href)
      }
    })
  }

  const renderProposalItem = (proposal: LeadProposal) => ({
    key: proposal?.id,
    showArrow: false,
    headerClass: "group",
    label: (
      <LeadViewProposalItem
        proposal={proposal}
        isUpdateAllowed={isUpdateAllowed}
        viewOnly={viewOnly}
        onViewProposal={onViewProposal}
        onViewPDF={setPreviewFile}
        onDuplicateProposal={onDuplicateProposal}
        onSendToReview={onSendToReview}
        onMarkAsSent={onMarkAsSent}
        onShare={onShare}
      />
    ),
    children: proposal?.attachments?.length ? (
      <LeadViewProposalAttachments
        attachments={proposal.attachments}
        onViewPDF={setPreviewFile}
      />
    ) : (
      <Empty description="No attachments" />
    )
  })

  return (
    <>
      {previewHTML && (
        <Suspense fallback={<Spin fullscreen spinning />}>
          <LeadViewProposalView
            title="Proposal Preview"
            onClose={() => setPreviewHTML(null)}
            previewHTML={previewHTML}
          />
        </Suspense>
      )}

      {previewFile && (
        <FilePreviewModal
          visible
          doc={{
            file: previewFile?.file_url,
            name: previewFile?.file_name,
            version: previewFile?.version
          }}
          onClose={() => setPreviewFile(null)}
        />
      )}

      <Collapse items={initialProposals?.map(renderProposalItem)} />

      {hasMore && (
        <>
          <div
            className="grid transition-all duration-300 ease-in-out"
            style={{
              gridTemplateRows: showAll ? "1fr" : "0fr"
            }}
          >
            <div className="overflow-hidden">
              <Collapse items={additionalProposals?.map(renderProposalItem)} />
            </div>
          </div>

          <Divider className="my-2">
            <Button
              type="link"
              onClick={() => setShowAll((prev) => !prev)}
              className="text-sm"
            >
              {showAll ? "Show less" : `Show more (${hiddenCount})`}
            </Button>
          </Divider>
        </>
      )}
    </>
  )
}
